/**
 * EmptyState — canonical empty-list layout per DESIGN_SYSTEM.md
 * §Component Patterns → Empty State. Copy tone: encourage the next step,
 * never apologise; no default copy — consumers pass i18n strings.
 */

import type { ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { colors, designTokens } from '../../theme/theme';

export interface EmptyStateProps {
  /** Material icon, auto-sized/coloured. Mutually exclusive with `illustration`. */
  icon?: ReactElement;
  /** Custom graphic rendered as-is inside the 80px badge; keep under 56×56. */
  illustration?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Primary action (GradientCTA/Button), centred below the description. */
  cta?: ReactNode;
  /** Override outer vertical padding (MUI spacing units). Default: 12. */
  py?: number | string;
  /** Escape hatch for outer Box. */
  sx?: SxProps<Theme>;
}

export default function EmptyState({
  icon,
  illustration,
  title,
  description,
  cta,
  py = 12,
  sx,
}: EmptyStateProps) {
  // Auto-style a Material icon: coral, fontSize 36
  const renderedIcon = isValidElement(icon)
    ? cloneElement(icon as ReactElement<{ sx?: SxProps<Theme> }>, {
        sx: {
          fontSize: 36,
          color: colors.primary.main,
          ...(((icon as ReactElement<{ sx?: SxProps<Theme> }>).props.sx as object) || {}),
        } as SxProps<Theme>,
      })
    : null;

  return (
    <Box
      sx={[
        { textAlign: 'center', py, px: 3 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: `${designTokens.radius.lg}px`,
          bgcolor: colors.primary.transparent,
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        {illustration ?? renderedIcon}
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: '#FFFFFF',
          mb: description ? 1 : 0,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body1"
          sx={{
            color: colors.neutral[400],
            maxWidth: 360,
            mx: 'auto',
          }}
        >
          {description}
        </Typography>
      )}
      {cta && <Box sx={{ mt: 3 }}>{cta}</Box>}
    </Box>
  );
}
